import { useState } from 'react';
import { ChevronLeft, ChevronRight, Home, Book } from 'lucide-react';
import { Link } from 'wouter';

export default function NeurodegenerativeDisease() {
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    // Slide 1: Title Slide
    {
      title: "Neurodegenerative Disease: A Longevity and Functional Medicine Approach to Health Optimization",
      subtitle: "Strategies for Prevention, Early Intervention, and Enhanced Healthspan",
      content: (
        <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
          <div className="bg-gradient-to-br from-blue-50 to-indigo-100 p-12 rounded-2xl shadow-lg max-w-4xl">
            <h1 className="text-4xl font-bold text-gray-900 mb-6">
              Neurodegenerative Disease: A Longevity and Functional Medicine Approach to Health Optimization
            </h1>
            <p className="text-xl text-gray-700 mb-8">
              Strategies for Prevention, Early Intervention, and Enhanced Healthspan
            </p>
            <div className="text-lg text-gray-600">
              A comprehensive guide to understanding and addressing neurodegenerative diseases through the lens of longevity medicine and functional approaches
            </div>
          </div>
        </div>
      )
    },

    // Slide 2: The Challenge of Neurodegenerative Diseases
    {
      title: "The Escalating Challenge of NDDs",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-4">
              <div className="bg-red-50 p-6 rounded-lg border-l-4 border-red-400">
                <h3 className="font-semibold text-red-800 mb-2">Current Reality</h3>
                <ul className="text-sm text-red-700 space-y-2">
                  <li>• NDDs (Alzheimer's, Parkinson's, etc.) are a growing global health crisis</li>
                  <li>• Profound societal & economic impact: Dementia costs projected in trillions by mid-century</li>
                </ul>
              </div>
              
              <div className="bg-orange-50 p-6 rounded-lg border-l-4 border-orange-400">
                <h3 className="font-semibold text-orange-800 mb-2">Traditional Approach Limitations</h3>
                <ul className="text-sm text-orange-700 space-y-2">
                  <li>• Primarily symptom management after manifestation</li>
                  <li>• Often fails to alter disease progression</li>
                  <li>• Efficacy wanes as disease advances</li>
                </ul>
              </div>

              <div className="bg-green-50 p-6 rounded-lg border-l-4 border-green-400">
                <h3 className="font-semibold text-green-800 mb-2">Critical Need</h3>
                <p className="text-sm text-green-700">
                  A paradigm shift from reactive management to proactive prevention and health optimization
                </p>
              </div>
            </div>

            <div className="flex justify-center">
              <svg width="400" height="200" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <marker id="arrow" markerWidth="10" markerHeight="10" refX="0" refY="3" orient="auto" markerUnits="strokeWidth">
                    <path d="M0,0 L0,6 L9,3 z" fill="#555" />
                  </marker>
                </defs>
                <line x1="50" y1="100" x2="350" y2="100" stroke="#ccc" strokeWidth="10" />
                <line x1="50" y1="100" x2="200" y2="100" stroke="orange" strokeWidth="10" />
                <circle cx="200" cy="100" r="8" fill="red" />
                <text x="50" y="80" fontFamily="Arial" fontSize="12" fill="#333">Healthy Aging</text>
                <text x="170" y="130" fontFamily="Arial" fontSize="12" fill="red">Symptom Onset</text>
                <text x="250" y="130" fontFamily="Arial" fontSize="12" fill="#555">Traditional Intervention Point</text>
                <text x="250" y="80" fontFamily="Arial" fontSize="12" fill="#333">Disease Progression</text>
                <line x1="340" y1="100" x2="350" y2="100" stroke="#555" strokeWidth="2" markerEnd="url(#arrow)" />
              </svg>
            </div>
          </div>
          <p className="text-center text-sm text-gray-600">Traditional interventions often begin late in the disease process</p>
        </div>
      )
    },

    // Slide 3: A New Paradigm
    {
      title: "The Longevity & Functional Medicine Approach",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-4">
              <div className="bg-blue-50 p-6 rounded-lg border-l-4 border-blue-400">
                <h3 className="font-semibold text-blue-800 mb-2">Longevity Medicine</h3>
                <p className="text-sm text-blue-700">
                  Focuses on identifying and implementing preventive strategies to delay age-related cognitive/functional decline
                </p>
              </div>
              
              <div className="bg-green-50 p-6 rounded-lg border-l-4 border-green-400">
                <h3 className="font-semibold text-green-800 mb-2">Functional Medicine</h3>
                <p className="text-sm text-green-700">
                  Systems-based approach to unearth and address root causes of physiological dysfunction
                </p>
              </div>

              <div className="bg-purple-50 p-6 rounded-lg border-l-4 border-purple-400">
                <h3 className="font-semibold text-purple-800 mb-2">Synergy Benefits</h3>
                <ul className="text-sm text-purple-700 space-y-1">
                  <li>• Enhance healthspan (the period of healthy life)</li>
                  <li>• Proactively delay or prevent NDD onset</li>
                  <li>• Improve quality of life for those at risk or in early stages</li>
                </ul>
              </div>
            </div>

            <div className="flex justify-center items-center">
              <svg width="400" height="200" xmlns="http://www.w3.org/2000/svg">
                <style>
                  {`.text { font-family: Arial, sans-serif; font-size: 12px; fill: #333; }
                   .title-text { font-size: 14px; font-weight: bold; }
                   .box { stroke: #007bff; stroke-width: 1.5; fill: #e7f3ff; }
                   .arrow { fill: #007bff; }`}
                </style>
                <rect x="20" y="40" width="150" height="60" rx="5" ry="5" className="box"/>
                <text x="45" y="65" className="title-text">Longevity Med</text>
                <text x="30" y="85" className="text">Delay Aging Decline</text>

                <rect x="230" y="40" width="150" height="60" rx="5" ry="5" className="box"/>
                <text x="245" y="65" className="title-text">Functional Med</text>
                <text x="240" y="85" className="text">Address Root Causes</text>

                <path d="M175 70 L225 70 M215 65 L225 70 L215 75 Z" stroke="#007bff" strokeWidth="1.5" fill="none" className="arrow"/>

                <rect x="100" y="130" width="200" height="50" rx="5" ry="5" fill="#d4edda" stroke="#28a745" strokeWidth="1.5"/>
                <text x="120" y="150" className="title-text" fill="#155724">Proactive NDD Prevention</text>
                <text x="135" y="170" className="text" fill="#155724">& Health Optimization</text>

                <path d="M95 105 Q 120 125 140 130" stroke="#007bff" strokeWidth="1.5" fill="none"/>
                <path d="M305 105 Q 280 125 260 130" stroke="#007bff" strokeWidth="1.5" fill="none"/>
              </svg>
            </div>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-center text-sm text-gray-700">
              <strong>Emphasis:</strong> Multidimensional interventions targeting cellular, biochemical, hormonal, and lifestyle factors
            </p>
          </div>
        </div>
      )
    },

    // Slide 4: Understanding NDDs
    {
      title: "Core Mechanisms Driving Neurodegeneration",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 p-6 rounded-lg">
            <p className="text-gray-700 mb-4">
              NDDs are complex; a systems-based view is crucial. The interplay involves cellular aging, biochemical imbalances, 
              neuroinflammation, hormonal dysregulation, and lifestyle factors. Many of these link to the "Hallmarks of Aging."
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Key Mechanisms We'll Explore:</h3>
              <div className="space-y-3">
                <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-red-400">
                  <h4 className="font-semibold text-red-700">1. Cellular Senescence & Neuroinflammation</h4>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-orange-400">
                  <h4 className="font-semibold text-orange-700">2. Autophagy Impairment & Proteinopathies</h4>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-yellow-400">
                  <h4 className="font-semibold text-yellow-700">3. Mitochondrial Dysfunction & Oxidative Stress</h4>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-green-400">
                  <h4 className="font-semibold text-green-700">4. Plasmalogen Deficiency</h4>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-blue-400">
                  <h4 className="font-semibold text-blue-700">5. Gut-Brain Axis Disruption</h4>
                </div>
              </div>
            </div>

            <div className="flex justify-center items-center">
              <svg width="300" height="300" xmlns="http://www.w3.org/2000/svg">
                <style>
                  {`.center-text { font-family: Arial, sans-serif; font-size: 14px; font-weight: bold; fill: #2c3e50; text-anchor: middle; }
                   .mechanism-text { font-family: Arial, sans-serif; font-size: 11px; fill: #34495e; text-anchor: middle; }
                   .connection-line { stroke: #7f8c8d; stroke-width: 2; stroke-opacity: 0.6; }`}
                </style>
                
                {/* Central node */}
                <circle cx="150" cy="150" r="40" fill="#e74c3c" stroke="#c0392b" strokeWidth="2"/>
                <text x="150" y="155" className="center-text" fill="white">Neuro-</text>
                <text x="150" y="170" className="center-text" fill="white">degeneration</text>
                
                {/* Surrounding mechanisms */}
                <circle cx="150" cy="50" r="35" fill="#3498db" stroke="#2980b9" strokeWidth="2"/>
                <text x="150" y="45" className="mechanism-text">Cellular</text>
                <text x="150" y="58" className="mechanism-text">Senescence</text>
                
                <circle cx="250" cy="100" r="35" fill="#e67e22" stroke="#d35400" strokeWidth="2"/>
                <text x="250" y="95" className="mechanism-text">Autophagy</text>
                <text x="250" y="108" className="mechanism-text">Impairment</text>
                
                <circle cx="250" cy="200" r="35" fill="#f39c12" stroke="#e67e22" strokeWidth="2"/>
                <text x="250" y="195" className="mechanism-text">Mitochondrial</text>
                <text x="250" y="208" className="mechanism-text">Dysfunction</text>
                
                <circle cx="150" cy="250" r="35" fill="#27ae60" stroke="#229954" strokeWidth="2"/>
                <text x="150" y="245" className="mechanism-text">Plasmalogen</text>
                <text x="150" y="258" className="mechanism-text">Deficiency</text>
                
                <circle cx="50" cy="200" r="35" fill="#8e44ad" stroke="#7d3c98" strokeWidth="2"/>
                <text x="50" y="195" className="mechanism-text">Gut-Brain</text>
                <text x="50" y="208" className="mechanism-text">Axis</text>
                
                {/* Connection lines */}
                <line x1="130" y1="110" x2="150" y2="85" className="connection-line"/>
                <line x1="190" y1="130" x2="215" y2="120" className="connection-line"/>
                <line x1="190" y1="170" x2="215" y2="180" className="connection-line"/>
                <line x1="150" y1="190" x2="150" y2="215" className="connection-line"/>
                <line x1="110" y1="170" x2="85" y2="180" className="connection-line"/>
                <line x1="110" y1="130" x2="85" y2="120" className="connection-line"/>
              </svg>
            </div>
          </div>
        </div>
      )
    },

    // Slide 5: Cellular Senescence & Neuroinflammation
    {
      title: "Senescence & Neuroinflammation: A Vicious Cycle",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-4">
              <div className="bg-red-50 p-6 rounded-lg border-l-4 border-red-400">
                <h3 className="font-semibold text-red-800 mb-3">Cellular Senescence</h3>
                <ul className="text-sm text-red-700 space-y-2">
                  <li>• Irreversible cell cycle arrest + pro-inflammatory secretions (SASP)</li>
                  <li>• Glial Cell Senescence (Astrocytes, Microglia): Key in NDDs</li>
                  <li>• Triggers: DNA damage, ROS, Aβ/Tau pathology</li>
                </ul>
              </div>
              
              <div className="bg-orange-50 p-6 rounded-lg border-l-4 border-orange-400">
                <h3 className="font-semibold text-orange-800 mb-3">SASP (Senescence-Associated Secretory Phenotype)</h3>
                <ul className="text-sm text-orange-700 space-y-2">
                  <li>• Releases IL-1α, IL-1β, IL-6, TNF-α, etc.</li>
                  <li>• Drives chronic neuroinflammation, BBB disruption, neuronal damage</li>
                  <li>• Senescent microglia: Reduced Aβ/tau clearance</li>
                </ul>
              </div>

              <div className="bg-blue-50 p-6 rounded-lg border-l-4 border-blue-400">
                <h3 className="font-semibold text-blue-800 mb-3">Neuroinflammation</h3>
                <p className="text-sm text-blue-700">
                  Sustained activation of microglia/astrocytes (NF-κB, NLRP3 inflammasome)
                </p>
              </div>

              <div className="bg-green-50 p-6 rounded-lg border-l-4 border-green-400">
                <h3 className="font-semibold text-green-800 mb-2">Clinical Relevance</h3>
                <p className="text-sm text-green-700">
                  Targeting senescent cells (senolytics) or SASP (senomorphics) is a novel therapeutic strategy
                </p>
              </div>
            </div>

            <div className="flex justify-center">
              <svg width="450" height="250" xmlns="http://www.w3.org/2000/svg">
                <style>
                  {`.text { font-family: Arial, sans-serif; font-size: 12px; fill: #333; }
                   .label { font-family: Arial, sans-serif; font-size: 10px; fill: #555; }
                   .neuron { fill: #87CEFA; stroke: #4682B4; stroke-width: 1.5; }
                   .glia { fill: #FFD700; stroke: #B8860B; stroke-width: 1.5; }
                   .senescent-glia { fill: #FFA07A; stroke: #CD5C5C; stroke-width: 1.5; }
                   .sasp { fill: #FF6347; opacity: 0.7; }
                   .arrow { fill: #CD5C5C; }`}
                </style>
                
                {/* Healthy Glia and Neuron */}
                <ellipse cx="70" cy="70" rx="30" ry="20" className="glia"/>
                <text x="50" y="75" className="label">Healthy Glia</text>
                <ellipse cx="150" cy="70" rx="35" ry="25" className="neuron"/>
                <text x="130" y="75" className="label">Neuron</text>

                {/* Stressors */}
                <text x="230" y="30" className="text">Stressors (Aβ, ROS, DNA damage)</text>
                <path d="M100 70 Q 160 40 220 50" stroke="#777" strokeWidth="1.5" fill="none" strokeDasharray="4 2"/>
                <path d="M210 45 L220 50 L215 55 Z" className="arrow" fill="#777"/>

                {/* Senescent Glia */}
                <ellipse cx="280" cy="100" rx="35" ry="25" className="senescent-glia"/>
                <text x="245" y="105" className="label">Senescent Glia</text>

                {/* SASP Cloud */}
                <ellipse cx="280" cy="100" rx="60" ry="45" className="sasp"/>
                <text x="260" y="95" className="label" fill="white">SASP</text>
                <text x="250" y="115" className="label" fill="white">(IL-6, TNF-α)</text>

                {/* SASP affecting Neuron */}
                <path d="M330 110 Q 350 140 300 170" stroke="#CD5C5C" strokeWidth="2" fill="none"/>
                <path d="M295 165 L300 170 L295 175 Z" className="arrow"/>
                <ellipse cx="250" cy="190" rx="35" ry="25" className="neuron" style={{opacity: 0.6}}/>
                <text x="220" y="195" className="label">Damaged Neuron</text>
                <text x="220" y="210" className="label">(Neuroinflammation)</text>
              </svg>
            </div>
          </div>
          <p className="text-center text-sm text-gray-600">
            Stressors induce glial senescence, leading to SASP release, neuroinflammation, and neuronal damage
          </p>
        </div>
      )
    },

    // Slide 6: Autophagy Impairment & Proteinopathies
    {
      title: "Autophagy: The Brain's Housekeeping System",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-4">
              <div className="bg-blue-50 p-6 rounded-lg border-l-4 border-blue-400">
                <h3 className="font-semibold text-blue-800 mb-3">Autophagy Function</h3>
                <ul className="text-sm text-blue-700 space-y-2">
                  <li>• Cellular process for degrading damaged proteins (Aβ, tau, α-synuclein) and organelles</li>
                  <li>• Essential for neuronal health</li>
                </ul>
              </div>
              
              <div className="bg-red-50 p-6 rounded-lg border-l-4 border-red-400">
                <h3 className="font-semibold text-red-800 mb-3">Decline with Age/NDDs</h3>
                <ul className="text-sm text-red-700 space-y-2">
                  <li>• Autophagic function is often impaired</li>
                  <li>• Vicious cycle: Toxic protein accumulation further hinders autophagy</li>
                  <li>• Linked to Aβ plaque accumulation, neuronal/glial senescence</li>
                </ul>
              </div>

              <div className="bg-green-50 p-6 rounded-lg border-l-4 border-green-400">
                <h3 className="font-semibold text-green-800 mb-3">Neuroplasticity</h3>
                <ul className="text-sm text-green-700 space-y-2">
                  <li>• Brain's ability to reorganize (BDNF, NGF)</li>
                  <li>• BDNF levels reduced in aging & NDDs, correlating with cognitive decline</li>
                </ul>
              </div>

              <div className="bg-purple-50 p-6 rounded-lg border-l-4 border-purple-400">
                <h3 className="font-semibold text-purple-800 mb-2">Clinical Relevance</h3>
                <p className="text-sm text-purple-700">
                  Enhancing autophagy and neurotrophic factor signaling are key therapeutic targets
                </p>
              </div>
            </div>

            <div className="flex justify-center">
              <svg width="450" height="220" xmlns="http://www.w3.org/2000/svg">
                <style>
                  {`.text { font-family: Arial, sans-serif; font-size: 12px; fill: #333; }
                   .label { font-family: Arial, sans-serif; font-size: 10px; fill: #555; }
                   .cell { stroke: #666; stroke-width: 1.5; fill: #f0f8ff; }
                   .waste { fill: #d3d3d3; }
                   .autophagosome { fill: #add8e6; stroke: #4682b4; }
                   .lysosome { fill: #ffb6c1; stroke: #db7093; }
                   .arrow { fill: #333; }`}
                </style>
                <defs>
                  <marker id="arrowhead-small" markerWidth="6" markerHeight="4" refX="0" refY="2" orient="auto" markerUnits="strokeWidth">
                    <path d="M0,0 L0,4 L6,2 z" fill="#333" />
                  </marker>
                </defs>
                
                {/* Healthy Autophagy */}
                <text x="50" y="20" className="text" fontWeight="bold">Healthy Autophagy</text>
                <rect x="20" y="30" width="180" height="120" rx="10" ry="10" className="cell"/>
                <circle cx="60" cy="60" r="10" className="waste"/>
                <text x="50" y="80" className="label">Damaged</text>
                <text x="50" y="90" className="label">Proteins</text>
                <ellipse cx="100" cy="70" rx="25" ry="15" className="autophagosome"/>
                <text x="80" y="73" className="label">Autophagosome</text>
                <path d="M70 65 L80 70" stroke="#333" strokeWidth="1.5" markerEnd="url(#arrowhead-small)"/>
                <circle cx="150" cy="90" r="15" className="lysosome"/>
                <text x="135" y="115" className="label">Lysosome</text>
                <path d="M120 75 L140 85" stroke="#333" strokeWidth="1.5" markerEnd="url(#arrowhead-small)"/>
                <text x="70" y="135" className="label" fill="green">Clearance</text>

                {/* Impaired Autophagy */}
                <text x="270" y="20" className="text" fontWeight="bold">Impaired Autophagy (NDD)</text>
                <rect x="240" y="30" width="180" height="120" rx="10" ry="10" className="cell"/>
                <circle cx="280" cy="60" r="10" className="waste"/>
                <circle cx="300" cy="70" r="8" className="waste"/>
                <circle cx="270" cy="80" r="12" className="waste"/>
                <circle cx="310" cy="90" r="10" className="waste"/>
                <text x="280" y="115" className="label" fill="red">Accumulation</text>
                <text x="285" y="130" className="label" fill="red">(Aβ, Tau)</text>
                <line x1="330" y1="70" x2="350" y2="50" stroke="red" strokeWidth="2"/>
                <line x1="330" y1="70" x2="350" y2="90" stroke="red" strokeWidth="2"/>
                <text x="340" y="40" className="label" fill="red">Blocked</text>

                {/* BDNF */}
                <text x="20" y="180" className="text">Neuroplasticity Support:</text>
                <text x="20" y="200" className="text">BDNF Levels:</text>
                <text x="120" y="200" className="text" fill="green">Healthy: Optimal</text>
                <text x="250" y="200" className="text" fill="red">NDD: Reduced (14)</text>
              </svg>
            </div>
          </div>
          <p className="text-center text-sm text-gray-600">
            Impaired autophagy leads to toxic protein accumulation. Reduced BDNF impairs neuroplasticity.
          </p>
        </div>
      )
    },

    // Slide 7: Mitochondrial Dysfunction & Oxidative Stress
    {
      title: "Energy Crisis: Mitochondria & Oxidative Stress",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-4">
              <div className="bg-yellow-50 p-6 rounded-lg border-l-4 border-yellow-400">
                <h3 className="font-semibold text-yellow-800 mb-3">Mitochondrial Role</h3>
                <ul className="text-sm text-yellow-700 space-y-2">
                  <li>• Powerhouses of neurons; high energy demand</li>
                  <li>• Dysfunction in NDDs: Impaired biogenesis, reduced ATP, increased ROS</li>
                  <li>• GH/IGF-1 axis influences mitochondrial biology (decline with age)</li>
                </ul>
              </div>
              
              <div className="bg-red-50 p-6 rounded-lg border-l-4 border-red-400">
                <h3 className="font-semibold text-red-800 mb-3">Oxidative Stress (OS)</h3>
                <ul className="text-sm text-red-700 space-y-2">
                  <li>• Imbalance between ROS production and antioxidant defenses</li>
                  <li>• Damages lipids, proteins, DNA. Neurons are highly vulnerable</li>
                  <li>• Key antioxidant: Glutathione (GSH) – often depleted in NDDs</li>
                </ul>
              </div>

              <div className="bg-green-50 p-6 rounded-lg border-l-4 border-green-400">
                <h3 className="font-semibold text-green-800 mb-2">Clinical Relevance</h3>
                <p className="text-sm text-green-700">
                  Supporting mitochondrial health (e.g., SS-31, CoQ10, PQQ) and enhancing antioxidant defenses (e.g., NAC, GSH) are crucial
                </p>
              </div>
            </div>

            <div className="flex justify-center">
              <svg width="400" height="230" xmlns="http://www.w3.org/2000/svg">
                <style>
                  {`.text { font-family: Arial, sans-serif; font-size: 12px; fill: #333; }
                   .label { font-family: Arial, sans-serif; font-size: 10px; fill: #555; }
                   .mito { fill: #FFDEAD; stroke: #D2691E; stroke-width: 1.5; }
                   .cristae { stroke: #D2691E; stroke-width: 1; fill:none; }
                   .ros { fill: red; font-size:14px; font-weight:bold; }
                   .atp { fill: green; font-size:14px; font-weight:bold; }`}
                </style>
                
                {/* Healthy Mitochondrion */}
                <text x="30" y="20" className="text" fontWeight="bold">Healthy Mitochondrion</text>
                <ellipse cx="100" cy="80" rx="60" ry="30" className="mito"/>
                <path d="M60 80 C 70 70, 80 90, 90 80 S 100 70, 110 80 S 120 90, 130 80" className="cristae"/>
                <text x="80" y="125" className="atp">ATP ↑</text>
                <text x="120" y="60" className="ros" style={{opacity: 0.3}}>ROS ↓</text>

                {/* Dysfunctional Mitochondrion */}
                <text x="220" y="20" className="text" fontWeight="bold">Dysfunctional (NDD)</text>
                <ellipse cx="300" cy="80" rx="60" ry="30" className="mito" style={{fill: "#FFEBCD", opacity: 0.7}}/>
                <path d="M260 80 C 270 75, 280 85, 290 80 S 300 75, 310 80 S 320 85, 330 80" className="cristae" style={{strokeDasharray: "3 2", opacity: 0.5}}/>
                <text x="280" y="125" className="atp" style={{fill: "red", opacity: 0.7}}>ATP ↓</text>
                <text x="320" y="60" className="ros">ROS ↑</text>
                <text x="250" y="150" className="label">Oxidative Stress</text>
                <text x="250" y="165" className="label">Neuronal Damage</text>

                <text x="20" y="190" className="text">Antioxidant Defense:</text>
                <text x="20" y="210" className="text">Glutathione (GSH):</text>
                <text x="150" y="210" className="text" fill="green">Healthy: Optimal</text>
                <text x="280" y="210" className="text" fill="red">NDD: Depleted (47)</text>
              </svg>
            </div>
          </div>
          <p className="text-center text-sm text-gray-600">
            Mitochondrial dysfunction in NDDs leads to decreased ATP, increased ROS, and oxidative stress
          </p>
        </div>
      )
    },

    // Slide 8: Plasmalogen Deficiency
    {
      title: "Plasmalogens: Critical Brain Lipids",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-4">
              <div className="bg-green-50 p-6 rounded-lg border-l-4 border-green-400">
                <h3 className="font-semibold text-green-800 mb-3">Plasmalogens Overview</h3>
                <ul className="text-sm text-green-700 space-y-2">
                  <li>• Unique ether phospholipids, abundant in brain, myelin, neuronal membranes (18-20% of total phospholipids)</li>
                </ul>
              </div>
              
              <div className="bg-blue-50 p-6 rounded-lg border-l-4 border-blue-400">
                <h3 className="font-semibold text-blue-800 mb-3">Key Roles</h3>
                <ul className="text-sm text-blue-700 space-y-2">
                  <li>• Membrane structure/fluidity</li>
                  <li>• Vesicular fusion (neurotransmitter release)</li>
                  <li>• Cholesterol efflux</li>
                  <li>• Potent endogenous antioxidants</li>
                </ul>
              </div>

              <div className="bg-red-50 p-6 rounded-lg border-l-4 border-red-400">
                <h3 className="font-semibold text-red-800 mb-3">Deficiency in NDDs</h3>
                <ul className="text-sm text-red-700 space-y-2">
                  <li>• Marked decrease in brain, CSF, serum of AD & PD patients</li>
                  <li>• Correlates with cognitive severity</li>
                  <li>• May reduce γ-secretase activity (lowering Aβ), protect from apoptosis</li>
                </ul>
              </div>

              <div className="bg-purple-50 p-6 rounded-lg border-l-4 border-purple-400">
                <h3 className="font-semibold text-purple-800 mb-2">Clinical Relevance</h3>
                <p className="text-sm text-purple-700">
                  Modifiable factor. Supplementation with precursors (e.g., DHA-AAG, scallop-derived) shows promise in increasing levels and improving cognition in MCI/AD
                </p>
              </div>
            </div>

            <div className="flex justify-center">
              <svg width="450" height="230" xmlns="http://www.w3.org/2000/svg">
                <style>
                  {`.text { font-family: Arial, sans-serif; font-size: 12px; fill: #333; }
                   .label { font-family: Arial, sans-serif; font-size: 10px; fill: #555; }
                   .membrane { fill: #B0E0E6; }
                   .phospholipid { fill: #FFFACD; stroke: #F0E68C; }
                   .plasmalogen { fill: #98FB98; stroke: #3CB371; }
                   .head { fill: #87CEEB; }
                   .tail { stroke: #4682B4; stroke-width:1; }`}
                </style>
                
                <text x="20" y="20" className="text" fontWeight="bold">Neuronal Cell Membrane</text>

                {/* Healthy Membrane */}
                <text x="60" y="45" className="label">Healthy (High Plasmalogens)</text>
                <rect x="30" y="50" width="180" height="60" className="membrane"/>
                
                {/* Phospholipids */}
                <circle cx="50" cy="60" r="7" className="head"/>
                <line x1="50" y1="67" x2="45" y2="90" className="tail"/>
                <line x1="50" y1="67" x2="55" y2="90" className="tail"/>
                <circle cx="50" cy="100" r="7" className="head"/>
                <line x1="50" y1="93" x2="45" y2="70" className="tail"/>
                <line x1="50" y1="93" x2="55" y2="70" className="tail"/>
                
                {/* Plasmalogens (Green) */}
                <circle cx="80" cy="60" r="7" className="plasmalogen"/>
                <line x1="80" y1="67" x2="75" y2="90" className="tail"/>
                <line x1="80" y1="67" x2="85" y2="90" className="tail"/>
                <circle cx="80" cy="100" r="7" className="plasmalogen"/>
                <line x1="80" y1="93" x2="75" y2="70" className="tail"/>
                <line x1="80" y1="93" x2="85" y2="70" className="tail"/>
                <text x="40" y="125" className="label">Fluid, Functional, Protected</text>

                {/* Deficient Membrane */}
                <text x="270" y="45" className="label">NDD (Low Plasmalogens)</text>
                <rect x="240" y="50" width="180" height="60" className="membrane" style={{opacity: 0.7}}/>
                <circle cx="260" cy="60" r="7" className="head"/>
                <line x1="260" y1="67" x2="255" y2="90" className="tail"/>
                <line x1="260" y1="67" x2="265" y2="90" className="tail"/>
                <circle cx="260" cy="100" r="7" className="head"/>
                <line x1="260" y1="93" x2="255" y2="70" className="tail"/>
                <line x1="260" y1="93" x2="265" y2="70" className="tail"/>
                
                {/* Fewer Plasmalogens */}
                <circle cx="290" cy="60" r="7" className="plasmalogen" style={{opacity: 0.5}}/>
                <line x1="290" y1="67" x2="285" y2="90" className="tail"/>
                <line x1="290" y1="67" x2="295" y2="90" className="tail"/>
                <text x="250" y="125" className="label" fill="red">Rigid, Dysfunctional, Vulnerable</text>

                <text x="20" y="160" className="text">Serum Plasmalogens (PlsEtn):</text>
                <text x="20" y="180" className="text" fill="green">Healthy: Optimal</text>
                <text x="20" y="200" className="text" fill="red">AD/PD: Significantly Decreased (31, 35)</text>
                <text x="20" y="220" className="text">Therapeutic Goal: Restore Levels</text>
              </svg>
            </div>
          </div>
          <p className="text-center text-sm text-gray-600">
            Plasmalogens are vital for neuronal membrane health; deficiency is common in NDDs
          </p>
        </div>
      )
    },

    // Slide 9: Gut-Brain Axis & Microbiome
    {
      title: "The Gut-Brain Connection in NDDs",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-4">
              <div className="bg-blue-50 p-6 rounded-lg border-l-4 border-blue-400">
                <h3 className="font-semibold text-blue-800 mb-3">Gut-Brain Axis</h3>
                <p className="text-sm text-blue-700">
                  Bidirectional communication between GI tract and CNS
                </p>
              </div>
              
              <div className="bg-red-50 p-6 rounded-lg border-l-4 border-red-400">
                <h3 className="font-semibold text-red-800 mb-3">Gut Microbiome Dysbiosis</h3>
                <p className="text-sm text-red-700">
                  Imbalance in gut microbes implicated in AD & PD
                </p>
              </div>

              <div className="bg-orange-50 p-6 rounded-lg border-l-4 border-orange-400">
                <h3 className="font-semibold text-orange-800 mb-3">Mechanisms</h3>
                <ul className="text-sm text-orange-700 space-y-2">
                  <li>• <strong>Increased Neuroinflammation:</strong> "Leaky gut" allows LPS (endotoxin) into circulation → systemic inflammation → neuroinflammation</li>
                  <li>• <strong>Altered Metabolites:</strong> Reduced Short-Chain Fatty Acids (SCFAs) like butyrate (neuroprotective, anti-inflammatory)</li>
                  <li>• <strong>Vagal Nerve Signaling & Immune Modulation</strong></li>
                </ul>
              </div>

              <div className="bg-green-50 p-6 rounded-lg border-l-4 border-green-400">
                <h3 className="font-semibold text-green-800 mb-2">Clinical Relevance</h3>
                <p className="text-sm text-green-700">
                  Restoring gut health (diet, pre/probiotics) is a key adjunctive therapy. Lithium orotate may also enhance microbiome health and SCFAs
                </p>
              </div>
            </div>

            <div className="flex justify-center">
              <svg width="400" height="250" xmlns="http://www.w3.org/2000/svg">
                <style>
                  {`.text { font-family: Arial, sans-serif; font-size: 12px; fill: #333; }
                   .label { font-family: Arial, sans-serif; font-size: 10px; fill: #555; }
                   .brain { fill: #ADD8E6; stroke: #4682B4; }
                   .gut { fill: #90EE90; stroke: #2E8B57; }
                   .arrow { stroke: #555; stroke-width: 1.5; fill:none; marker-end: url(#arrow-marker);}
                   .bidirectional-arrow { stroke: #555; stroke-width: 1.5; fill:none; marker-start: url(#arrow-marker); marker-end: url(#arrow-marker);}`}
                </style>
                <defs>
                  <marker id="arrow-marker" markerWidth="8" markerHeight="8" refX="0" refY="3" orient="auto" markerUnits="strokeWidth">
                    <path d="M0,0 L0,6 L6,3 z" fill="#555" />
                  </marker>
                </defs>

                {/* Brain */}
                <ellipse cx="200" cy="60" rx="80" ry="40" className="brain"/>
                <text x="180" y="65" className="label">BRAIN</text>

                {/* Gut */}
                <ellipse cx="200" cy="190" rx="100" ry="30" className="gut"/>
                <text x="180" y="195" className="label">GUT (Microbiome)</text>

                {/* Vagus Nerve */}
                <path d="M200 100 C 170 120, 170 140, 200 160" className="bidirectional-arrow"/>
                <text x="120" y="135" className="label">Vagus Nerve</text>

                {/* Signaling Arrows */}
                <text x="280" y="110" className="label">Neurotransmitters</text>
                <path d="M220 105 Q 250 125 220 145" className="arrow"/>
                <text x="80" y="110" className="label">LPS (Dysbiosis) ↑</text>
                <text x="80" y="125" className="label" fill="red">Inflammation ↑</text>
                <path d="M180 145 Q 150 125 180 105" className="arrow" style={{stroke: "red"}}/>

                <text x="280" y="150" className="label">SCFAs (Butyrate) ↓</text>
                <text x="280" y="165" className="label" fill="green">Neuroprotection ↓</text>
                <path d="M180 150 Q 150 130 180 110" className="arrow" style={{stroke: "green", strokeDasharray: "3 2"}}/>

                <text x="50" y="235" className="text">Dysbiosis → Leaky Gut → Systemic Inflammation → Neuroinflammation (65)</text>
              </svg>
            </div>
          </div>
          <p className="text-center text-sm text-gray-600">
            The Gut-Brain Axis: Dysbiosis can drive neuroinflammation and impact brain health
          </p>
        </div>
      )
    },

    // Slide 10: Biomarker-Driven Approach
    {
      title: "Guiding Therapy with Biomarkers",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 p-8 rounded-lg">
            <h3 className="text-xl font-semibold text-blue-800 mb-4">Essential for Precision Medicine</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-green-400">
                  <h4 className="font-semibold text-green-700 mb-2">Early Detection & Risk Stratification</h4>
                  <p className="text-sm text-green-600">Identify issues before clinical symptoms</p>
                </div>
                
                <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-blue-400">
                  <h4 className="font-semibold text-blue-700 mb-2">Personalization</h4>
                  <p className="text-sm text-blue-600">Tailor interventions to individual biochemistry</p>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-purple-400">
                  <h4 className="font-semibold text-purple-700 mb-2">Monitoring Efficacy</h4>
                  <p className="text-sm text-purple-600">Objectively track response to therapy</p>
                </div>
                
                <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-orange-400">
                  <h4 className="font-semibold text-orange-700 mb-2">Multi-Systems Approach</h4>
                  <p className="text-sm text-orange-600">Provides insights into underlying mechanisms</p>
                </div>
              </div>
            </div>
            
            <div className="mt-6 text-center">
              <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4 rounded-lg">
                <h4 className="text-lg font-semibold">Goal: Move from "one-size-fits-all" to individualized protocols</h4>
              </div>
            </div>
          </div>

          <div className="flex justify-center">
            <svg width="300" height="200" xmlns="http://www.w3.org/2000/svg">
              <style>
                {`.magnify { fill: none; stroke: #2563eb; stroke-width: 3; }
                 .dna { stroke: #7c3aed; stroke-width: 2; fill: none; }
                 .text { font-family: Arial, sans-serif; font-size: 14px; fill: #374151; text-anchor: middle; }`}
              </style>
              
              {/* Magnifying glass */}
              <circle cx="120" cy="80" r="40" className="magnify"/>
              <line x1="150" y1="110" x2="180" y2="140" className="magnify"/>
              
              {/* DNA strand inside magnifying glass */}
              <path d="M90 70 Q 120 60, 150 70 Q 120 80, 90 70" className="dna"/>
              <path d="M90 90 Q 120 100, 150 90 Q 120 80, 90 90" className="dna"/>
              <line x1="95" y1="75" x2="95" y2="85" stroke="#7c3aed" strokeWidth="1"/>
              <line x1="120" y1="70" x2="120" y2="90" stroke="#7c3aed" strokeWidth="1"/>
              <line x1="145" y1="75" x2="145" y2="85" stroke="#7c3aed" strokeWidth="1"/>
              
              <text x="150" y="180" className="text">Detailed Biochemical Investigation</text>
            </svg>
          </div>
        </div>
      )
    },

    // Slide 11: Key Biomarkers Table
    {
      title: "Key Biomarkers for NDD Assessment",
      content: (
        <div className="space-y-6">
          <p className="text-center text-gray-700">A comprehensive panel offers a systemic biochemical snapshot</p>
          
          <div className="overflow-x-auto">
            <svg width="700" height="300" xmlns="http://www.w3.org/2000/svg">
              <style>
                {`.header { font-family: Arial, sans-serif; font-size: 13px; font-weight: bold; fill: #2c3e50; }
                 .cell { font-family: Arial, sans-serif; font-size: 12px; fill: #34495e; }
                 .row-bg-even { fill: #f8f9f9; }
                 .row-bg-odd { fill: #e8edef; }
                 .border { stroke: #bdc3c7; stroke-width: 1px; }`}
              </style>
              
              <rect x="10" y="10" width="680" height="30" className="row-bg-even" />
              <text x="20" y="30" className="header">Category</text>
              <text x="180" y="30" className="header">Biomarker Examples</text>
              <text x="370" y="30" className="header">Sample</text>
              <text x="450" y="30" className="header">Clinical Utility</text>

              {/* Row 1 */}
              <rect x="10" y="40" width="680" height="45" className="row-bg-odd" />
              <text x="20" y="60" className="cell">Membrane Integrity</text>
              <text x="180" y="60" className="cell">Ethanolamine Plasmalogens</text>
              <text x="180" y="75" className="cell">(e.g., PlsEtn C16:0/22:6)</text>
              <text x="370" y="60" className="cell">Serum, RBCs</text>
              <text x="450" y="60" className="cell">Deficiency linked to AD/PD severity;</text>
              <text x="450" y="75" className="cell">guide precursor supplementation. (31)</text>

              {/* Row 2 */}
              <rect x="10" y="85" width="680" height="45" className="row-bg-even" />
              <text x="20" y="105" className="cell">Oxidative Stress</text>
              <text x="180" y="105" className="cell">MDA, F2-Isoprostanes, 8-OHdG,</text>
              <text x="180" y="120" className="cell">GSH/GSSG Ratio</text>
              <text x="370" y="105" className="cell">Serum, Plasma,</text>
              <text x="370" y="120" className="cell">Urine</text>
              <text x="450" y="105" className="cell">Reflect lipid peroxidation, DNA damage,</text>
              <text x="450" y="120" className="cell">redox status; guide antioxidant therapies. (40)</text>

              {/* Row 3 */}
              <rect x="10" y="130" width="680" height="45" className="row-bg-odd" />
              <text x="20" y="150" className="cell">Inflammation</text>
              <text x="180" y="150" className="cell">hs-CRP, IL-6, TNF-α</text>
              <text x="370" y="150" className="cell">Serum, Plasma</text>
              <text x="450" y="150" className="cell">Indicate systemic/neuroinflammation;</text>
              <text x="450" y="165" className="cell">guide anti-inflammatory strategies. (3)</text>

              {/* Row 4 */}
              <rect x="10" y="175" width="680" height="45" className="row-bg-even" />
              <text x="20" y="195" className="cell">Metabolic Health</text>
              <text x="180" y="195" className="cell">Fasting Insulin, Glucose, HbA1c,</text>
              <text x="180" y="210" className="cell">HOMA-IR</text>
              <text x="370" y="195" className="cell">Blood</text>
              <text x="450" y="195" className="cell">Assess insulin resistance ("Type 3</text>
              <text x="450" y="210" className="cell">Diabetes"), a key NDD driver. (71)</text>

              {/* Row 5 */}
              <rect x="10" y="220" width="680" height="45" className="row-bg-odd" />
              <text x="20" y="240" className="cell">Genetic Risk</text>
              <text x="180" y="240" className="cell">APOE Genotype, PRSs</text>
              <text x="370" y="240" className="cell">Blood, Saliva</text>
              <text x="450" y="240" className="cell">Stratify AD risk (APOE4); inform</text>
              <text x="450" y="255" className="cell">intensity of preventive strategies. (73, 75)</text>

              <line x1="10" y1="10" x2="10" y2="265" className="border"/>
              <line x1="170" y1="10" x2="170" y2="265" className="border"/>
              <line x1="360" y1="10" x2="360" y2="265" className="border"/>
              <line x1="440" y1="10" x2="440" y2="265" className="border"/>
              <line x1="690" y1="10" x2="690" y2="265" className="border"/>
              <line x1="10" y1="10" x2="690" y2="10" className="border"/>
              <line x1="10" y1="40" x2="690" y2="40" className="border"/>
              <line x1="10" y1="85" x2="690" y2="85" className="border"/>
              <line x1="10" y1="130" x2="690" y2="130" className="border"/>
              <line x1="10" y1="175" x2="690" y2="175" className="border"/>
              <line x1="10" y1="220" x2="690" y2="220" className="border"/>
              <line x1="10" y1="265" x2="690" y2="265" className="border"/>
            </svg>
          </div>
          
          <div className="bg-blue-50 p-4 rounded-lg">
            <p className="text-sm text-blue-700">
              <strong>Additional Categories:</strong> Neurotrophic Support (BDNF), AD Pathology (CSF markers/PET, plasma p-tau217), 
              Hormonal Balance, Gut-Brain Axis (Stool analysis, SCFAs). Comprehensive panels like ProdromeScan™ available for extensive testing.
            </p>
          </div>
        </div>
      )
    },

    // Slide 12: Interpreting Biomarkers Example
    {
      title: "Interpreting Biomarkers: Example - Plasmalogens",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div className="bg-red-50 p-6 rounded-lg border-l-4 border-red-400">
                <h3 className="font-semibold text-red-800 mb-3">Finding</h3>
                <p className="text-sm text-red-700">
                  Low serum ethanolamine plasmalogens (e.g., PlsEtn 16:0/22:6)
                </p>
              </div>
              
              <div className="bg-blue-50 p-6 rounded-lg border-l-4 border-blue-400">
                <h3 className="font-semibold text-blue-800 mb-3">Interpretation</h3>
                <ul className="text-sm text-blue-700 space-y-2">
                  <li>• Indicates deficiency in critical brain phospholipids</li>
                  <li>• Associated with impaired membrane function, reduced antioxidant capacity</li>
                  <li>• Increased risk/severity of AD, PD, cognitive decline (31, 32)</li>
                  <li>• May reflect impaired peroxisomal function or increased oxidative degradation</li>
                </ul>
              </div>

              <div className="bg-green-50 p-6 rounded-lg border-l-4 border-green-400">
                <h3 className="font-semibold text-green-800 mb-3">Clinical Action</h3>
                <ul className="text-sm text-green-700 space-y-2">
                  <li>• Supplement with plasmalogen precursors (e.g., DHA-AAG, scallop-derived)</li>
                  <li>• Titrate dose based on follow-up blood levels</li>
                  <li>• Monitor related markers (e.g., MDA, CAT, SOD activity)</li>
                  <li>• Address underlying factors contributing to deficiency</li>
                </ul>
              </div>
            </div>

            <div className="flex justify-center items-center">
              <svg width="350" height="400" xmlns="http://www.w3.org/2000/svg">
                <style>
                  {`.flowchart-box { fill: #f8f9fa; stroke: #6c757d; stroke-width: 2; }
                   .finding-box { fill: #f8d7da; stroke: #dc3545; }
                   .interpretation-box { fill: #d1ecf1; stroke: #17a2b8; }
                   .action-box { fill: #d4edda; stroke: #28a745; }
                   .flowchart-text { font-family: Arial, sans-serif; font-size: 12px; fill: #333; text-anchor: middle; }
                   .arrow-line { stroke: #6c757d; stroke-width: 2; marker-end: url(#flowchart-arrow); }`}
                </style>
                <defs>
                  <marker id="flowchart-arrow" markerWidth="10" markerHeight="10" refX="0" refY="3" orient="auto" markerUnits="strokeWidth">
                    <path d="M0,0 L0,6 L9,3 z" fill="#6c757d" />
                  </marker>
                </defs>

                {/* Finding */}
                <rect x="50" y="30" width="250" height="60" rx="10" className="finding-box"/>
                <text x="175" y="50" className="flowchart-text" fontWeight="bold">Low Plasmalogen Test Result</text>
                <text x="175" y="70" className="flowchart-text">(PlsEtn 16:0/22:6 ↓)</text>

                {/* Arrow */}
                <line x1="175" y1="90" x2="175" y2="120" className="arrow-line"/>

                {/* Interpretation */}
                <rect x="50" y="130" width="250" height="100" rx="10" className="interpretation-box"/>
                <text x="175" y="150" className="flowchart-text" fontWeight="bold">Interpretation</text>
                <text x="175" y="170" className="flowchart-text">• Membrane dysfunction</text>
                <text x="175" y="185" className="flowchart-text">• Reduced antioxidant capacity</text>
                <text x="175" y="200" className="flowchart-text">• Increased NDD risk</text>
                <text x="175" y="215" className="flowchart-text">• Peroxisomal impairment</text>

                {/* Arrow */}
                <line x1="175" y1="230" x2="175" y2="260" className="arrow-line"/>

                {/* Clinical Actions */}
                <rect x="50" y="270" width="250" height="100" rx="10" className="action-box"/>
                <text x="175" y="290" className="flowchart-text" fontWeight="bold">Clinical Actions</text>
                <text x="175" y="310" className="flowchart-text">• Plasmalogen precursors</text>
                <text x="175" y="325" className="flowchart-text">• Dose titration</text>
                <text x="175" y="340" className="flowchart-text">• Monitor related markers</text>
                <text x="175" y="355" className="flowchart-text">• Address root causes</text>
              </svg>
            </div>
          </div>
        </div>
      )
    },

    // Continue creating more slides...
    // Due to length limitations, I'll create a condensed version with key remaining slides

    // Slide 13: Therapeutic Strategies - Foundational Lifestyle
    {
      title: "Foundation First: Therapeutic Lifestyle Changes (TLCs)",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 p-6 rounded-lg border-l-4 border-blue-400">
            <h3 className="text-lg font-semibold text-blue-800 mb-3">The 80/20 Rule</h3>
            <p className="text-blue-700">
              ~80% of long-term health outcomes driven by consistent lifestyle. Supplements/meds are adjuncts.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-green-400">
              <div className="flex items-center mb-3">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3">
                  🥗
                </div>
                <h4 className="font-semibold text-green-700">Diet</h4>
              </div>
              <ul className="text-sm text-green-600 space-y-1">
                <li>• Anti-inflammatory (Mediterranean, MIND diet)</li>
                <li>• Low-glycemic, whole foods</li>
                <li>• Consider mildly ketogenic approaches</li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-blue-400">
              <div className="flex items-center mb-3">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                  🏃
                </div>
                <h4 className="font-semibold text-blue-700">Exercise</h4>
              </div>
              <ul className="text-sm text-blue-600 space-y-1">
                <li>• Aerobic (improves blood flow, BDNF)</li>
                <li>• Resistance training (muscle, insulin sensitivity)</li>
                <li>• Balance & coordination</li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-purple-400">
              <div className="flex items-center mb-3">
                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                  🌙
                </div>
                <h4 className="font-semibold text-purple-700">Sleep</h4>
              </div>
              <ul className="text-sm text-purple-600 space-y-1">
                <li>• Crucial for glymphatic clearance (Aβ removal)</li>
                <li>• Memory consolidation</li>
                <li>• Aim for 7-9 hours</li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-orange-400">
              <div className="flex items-center mb-3">
                <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center mr-3">
                  🧘
                </div>
                <h4 className="font-semibold text-orange-700">Stress Management</h4>
              </div>
              <ul className="text-sm text-orange-600 space-y-1">
                <li>• Chronic stress exacerbates inflammation</li>
                <li>• Cortisol dysregulation</li>
                <li>• Meditation, yoga, mindfulness</li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-yellow-400">
              <div className="flex items-center mb-3">
                <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center mr-3">
                  ☀️
                </div>
                <h4 className="font-semibold text-yellow-700">Circadian Rhythm</h4>
              </div>
              <ul className="text-sm text-yellow-600 space-y-1">
                <li>• Light exposure optimization</li>
                <li>• Regular meal times</li>
                <li>• Sleep-wake cycle regulation</li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-red-400">
              <div className="flex items-center mb-3">
                <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center mr-3">
                  🚫
                </div>
                <h4 className="font-semibold text-red-700">Toxin Management</h4>
              </div>
              <ul className="text-sm text-red-600 space-y-1">
                <li>• Toxin avoidance</li>
                <li>• Detoxification support</li>
                <li>• Environmental optimization</li>
              </ul>
            </div>
          </div>
        </div>
      )
    },

    // Slide 14: Targeted Interventions - Plasmalogen Restoration
    {
      title: "Intervention Example: Plasmalogen Precursors",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 p-6 rounded-lg border-l-4 border-blue-400">
            <h3 className="font-semibold text-blue-800 mb-3">Rationale</h3>
            <p className="text-sm text-blue-700">
              Correct documented deficiency, support membrane function, reduce Aβ, antioxidant properties
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-4">
              <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-green-400">
                <h4 className="font-semibold text-green-700 mb-3">DHA-AAG</h4>
                <ul className="text-sm text-green-600 space-y-2">
                  <li>• Escalating oral dose: 900 mg/day (M1) → 1800 mg/day (M2-3) → 3600 mg/day (M4)</li>
                  <li>• Study (Wood et al., 2022): 22 cognitively impaired individuals</li>
                  <li>• Increased blood DHA PlsEtn, normalized oxidative stress markers (CAT, MDA, SOD)</li>
                  <li>• Small N, open-label study</li>
                </ul>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-purple-400">
                <h4 className="font-semibold text-purple-700 mb-3">Scallop-derived</h4>
                <ul className="text-sm text-purple-600 space-y-2">
                  <li>• 1 mg/day (0.5 mg bid) for 24 weeks</li>
                  <li>• RCT (Fujino et al., 2017, 2021): Mild AD/MCI</li>
                  <li>• Maintained plasma PlsPE in mild AD</li>
                  <li>• Subgroup (mild AD, female, {'<'}77y) showed memory improvement (WMS-R)</li>
                </ul>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-orange-400">
                <h4 className="font-semibold text-orange-700 mb-3">PPI-1011 (Synthetic)</h4>
                <p className="text-sm text-orange-600">
                  Phase I showed dose-dependent increases in serum PlsEtn
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="font-semibold text-gray-800 mb-3">Monitoring & Titration</h4>
                <ul className="text-sm text-gray-700 space-y-2">
                  <li>• Blood Plasmalogen Levels (e.g., ProdromeScan™): Key to guide dosing</li>
                  <li>• Aim to restore to optimal/youthful range</li>
                  <li>• Oxidative stress markers (MDA, CAT, SOD)</li>
                  <li>• Cognitive assessments</li>
                </ul>
              </div>

              <div className="flex justify-center items-center p-8">
                <svg width="200" height="150" xmlns="http://www.w3.org/2000/svg">
                  <style>
                    {`.supplement { fill: #22c55e; stroke: #16a34a; stroke-width: 2; }
                     .test-tube { fill: #3b82f6; stroke: #1d4ed8; stroke-width: 2; }
                     .brain { fill: #8b5cf6; stroke: #7c3aed; stroke-width: 2; }
                     .text-label { font-family: Arial, sans-serif; font-size: 10px; fill: #374151; text-anchor: middle; }`}
                  </style>
                  
                  <rect x="20" y="20" width="40" height="60" rx="5" className="supplement"/>
                  <text x="40" y="95" className="text-label">Supplement</text>
                  
                  <rect x="80" y="30" width="15" height="50" rx="3" className="test-tube"/>
                  <circle cx="87.5" cy="25" r="8" className="test-tube"/>
                  <text x="87.5" y="95" className="text-label">Blood Test</text>
                  
                  <ellipse cx="150" cy="50" rx="30" ry="20" className="brain"/>
                  <text x="150" y="95" className="text-label">Healthy Brain</text>
                </svg>
              </div>
            </div>
          </div>
        </div>
      )
    },

    // Slide 15: Mitochondrial & Antioxidant Support
    {
      title: "Energizing Neurons: Mitochondrial & Antioxidant Support",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 p-6 rounded-lg border-l-4 border-blue-400">
            <h3 className="font-semibold text-blue-800 mb-3">Rationale</h3>
            <p className="text-sm text-blue-700">
              Counteract energy deficits and oxidative damage in NDDs
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-4">
              <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-yellow-400">
                <h4 className="font-semibold text-yellow-700 mb-3">Mitochondrial Support</h4>
                <div className="space-y-3">
                  <div className="bg-yellow-50 p-3 rounded">
                    <h5 className="font-medium text-yellow-800">SS-31 (Elamipretide)</h5>
                    <ul className="text-xs text-yellow-700 space-y-1 mt-2">
                      <li>• Targets cardiolipin, stabilizes cristae, ↑ATP, ↓ROS</li>
                      <li>• Dosing: IV/SC (e.g., 40mg/day SC)</li>
                      <li>• Preclinical: Improves mitochondrial function, reduces oxidative stress/inflammation in NDD models</li>
                      <li>• Note: Costly intervention</li>
                    </ul>
                  </div>
                  <div className="bg-yellow-50 p-3 rounded">
                    <h5 className="font-medium text-yellow-800">Other Agents</h5>
                    <p className="text-xs text-yellow-700">CoQ10, PQQ, L-Carnitine, Alpha-Lipoic Acid: Support ETC, antioxidant</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-green-400">
                <h4 className="font-semibold text-green-700 mb-3">Antioxidant Support (Glutathione System)</h4>
                <div className="space-y-3">
                  <div className="bg-green-50 p-3 rounded">
                    <h5 className="font-medium text-green-800">N-Acetylcysteine (NAC)</h5>
                    <ul className="text-xs text-green-700 space-y-1 mt-2">
                      <li>• GSH precursor</li>
                      <li>• Dosing: Oral 600-1200mg bid-tid. IV for higher bioavailability</li>
                      <li>• PD Study (Monti et al.): IV NAC + oral NAC showed increased brain GSH and improved UPDRS scores</li>
                    </ul>
                  </div>
                  <div className="bg-green-50 p-3 rounded">
                    <h5 className="font-medium text-green-800">Liposomal Glutathione</h5>
                    <ul className="text-xs text-green-700 space-y-1 mt-2">
                      <li>• Enhanced oral absorption</li>
                      <li>• Dosing: 250-500mg bid</li>
                      <li>• Study (Richie et al., 2015): 500-1000mg/day increased body GSH, reduced OS markers</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-center">
            <svg width="400" height="150" xmlns="http://www.w3.org/2000/svg">
              <style>
                {`.mito-graphic { fill: #fbbf24; stroke: #d97706; stroke-width: 2; }
                 .shield { fill: #10b981; stroke: #059669; stroke-width: 2; }
                 .text-label { font-family: Arial, sans-serif; font-size: 12px; fill: #374151; text-anchor: middle; }`}
              </style>
              
              <ellipse cx="150" cy="75" rx="60" ry="40" className="mito-graphic"/>
              <text x="150" y="80" className="text-label">Mitochondrion</text>
              <text x="150" y="130" className="text-label">Energy Production</text>
              
              <circle cx="300" cy="75" r="40" className="shield"/>
              <text x="300" y="80" className="text-label">Antioxidant</text>
              <text x="300" y="95" className="text-label">Shield</text>
              <text x="300" y="130" className="text-label">ROS Protection</text>
            </svg>
          </div>
        </div>
      )
    },

    // Slide 16: Hormonal Optimization (BHRT)
    {
      title: "Restoring Balance: Bioidentical Hormone Replacement (BHRT)",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 p-6 rounded-lg border-l-4 border-blue-400">
            <h3 className="font-semibold text-blue-800 mb-3">Rationale</h3>
            <p className="text-sm text-blue-700">
              Age-related decline in sex hormones (estrogen, testosterone) & neurosteroids (DHEA, pregnenolone) contributes to neuronal vulnerability. 
              BHRT aims to restore youthful, physiological levels.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-4">
              <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-purple-400">
                <h4 className="font-semibold text-purple-700 mb-3">Key Principles</h4>
                <ul className="text-sm text-purple-600 space-y-2">
                  <li>• <strong>Comprehensive Testing:</strong> Serum, saliva, or urine (estradiol, progesterone, testosterone, DHEA-S, pregnenolone, cortisol, thyroid)</li>
                  <li>• <strong>Bioidentical Forms:</strong> Structurally identical to endogenous hormones</li>
                  <li>• <strong>Individualized Dosing & Route:</strong> Transdermal often preferred for E2/T (bypass first-pass)</li>
                  <li>• <strong>"Critical Window" Hypothesis:</strong> HRT (esp. estrogen) initiated early around menopause may offer greatest neuroprotective benefit vs AD</li>
                </ul>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-green-400">
                <h4 className="font-semibold text-green-700 mb-3">Examples (Individualize!)</h4>
                <ul className="text-sm text-green-600 space-y-2">
                  <li>• <strong>Estrogens (women):</strong> Bi-Est/Tri-Est transdermal. Progesterone (micronized oral/transdermal) if uterus intact</li>
                  <li>• <strong>Testosterone (men/women):</strong> Injections, gels, pellets. Low dose for women</li>
                  <li>• <strong>DHEA:</strong> 5-50 mg/day</li>
                  <li>• <strong>Pregnenolone:</strong> 5-30 mg/day (higher doses greater than 10mg may have more endocrine effects)</li>
                </ul>
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-orange-400">
                <h4 className="font-semibold text-orange-700 mb-3">Monitoring</h4>
                <ul className="text-sm text-orange-600 space-y-2">
                  <li>• Symptoms assessment</li>
                  <li>• Follow-up hormone levels</li>
                  <li>• Relevant safety markers (PSA, mammograms)</li>
                  <li>• Note: Adiposity influences dosing</li>
                </ul>
              </div>

              <div className="flex justify-center items-center p-8">
                <svg width="300" height="200" xmlns="http://www.w3.org/2000/svg">
                  <style>
                    {`.decline-line { stroke: #ef4444; stroke-width: 3; fill: none; }
                     .restore-line { stroke: #10b981; stroke-width: 3; fill: none; stroke-dasharray: 5,5; }
                     .text-label { font-family: Arial, sans-serif; font-size: 11px; fill: #374151; }
                     .axis { stroke: #6b7280; stroke-width: 1; }`}
                  </style>
                  
                  <line x1="50" y1="170" x2="250" y2="170" className="axis"/>
                  <line x1="50" y1="170" x2="50" y2="30" className="axis"/>
                  
                  <path d="M60 60 Q 100 80, 140 120 Q 180 140, 220 160" className="decline-line"/>
                  <path d="M140 120 Q 180 90, 220 80" className="restore-line"/>
                  
                  <text x="60" y="25" className="text-label">High</text>
                  <text x="60" y="180" className="text-label">Low</text>
                  <text x="60" y="195" className="text-label">Young</text>
                  <text x="220" y="195" className="text-label">Older</text>
                  
                  <text x="100" y="50" className="text-label" fill="#ef4444">Natural Decline</text>
                  <text x="160" y="70" className="text-label" fill="#10b981">BHRT Restoration</text>
                </svg>
              </div>
            </div>
          </div>
        </div>
      )
    },

    // Slide 17: Emerging Therapies - Peptides & Senolytics
    {
      title: "Frontier Therapies: Peptides & Senolytics",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-4">
              <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-blue-400">
                <h4 className="font-semibold text-blue-700 mb-3">Peptide Therapies</h4>
                <p className="text-sm text-blue-600 mb-3">Targeted modulation of physiological pathways</p>
                
                <div className="space-y-3">
                  <div className="bg-blue-50 p-3 rounded">
                    <h5 className="font-medium text-blue-800">CJC-1295/Ipamorelin (GH Secretagogues)</h5>
                    <ul className="text-xs text-blue-700 space-y-1 mt-2">
                      <li>• ↑GH/IGF-1 → cellular efficiency, autophagy, mitochondrial health, sleep</li>
                      <li>• Dosing: e.g., 100mcg each SC nightly, 5 days/week. Monitor IGF-1</li>
                    </ul>
                  </div>
                  
                  <div className="bg-blue-50 p-3 rounded">
                    <h5 className="font-medium text-blue-800">BPC-157</h5>
                    <ul className="text-xs text-blue-700 space-y-1 mt-2">
                      <li>• Tissue repair, anti-inflammatory, gut-brain axis support</li>
                      <li>• Dosing: 250-500mcg oral/SC daily. Preclinical NDD benefits</li>
                    </ul>
                  </div>
                  
                  <div className="bg-blue-50 p-3 rounded">
                    <h5 className="font-medium text-blue-800">Thymosin Alpha 1 (TA1)</h5>
                    <ul className="text-xs text-blue-700 space-y-1 mt-2">
                      <li>• Immunomodulation, reduces T-cell senescence</li>
                      <li>• Dosing: 1.5-1.6mg SC 2-3x/week</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-red-400">
                <h4 className="font-semibold text-red-700 mb-3">Senolytics</h4>
                <p className="text-sm text-red-600 mb-3">Selectively clear senescent cells, reduce SASP</p>
                
                <div className="space-y-3">
                  <div className="bg-red-50 p-3 rounded">
                    <h5 className="font-medium text-red-800">Dasatinib + Quercetin (D+Q), Fisetin</h5>
                    <p className="text-xs text-red-700">Intermittent pulsing protocols</p>
                  </div>
                  
                  <div className="bg-red-50 p-3 rounded">
                    <h5 className="font-medium text-red-800">Azithromycin (Repurposed)</h5>
                    <ul className="text-xs text-red-700 space-y-1 mt-2">
                      <li>• Senolytic in fibroblasts</li>
                      <li>• Potential Protocol: Low-dose Z-PAK (500mg D1, 250mg D2-5) periodically (e.g., q1-3 months)</li>
                      <li>• Rationale: Long half-life, reduce senescent glial burden/neuroinflammation</li>
                      <li>• Consider antibiotic stewardship. Neuroprotective in TBI rat model (high dose)</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-center">
            <svg width="400" height="150" xmlns="http://www.w3.org/2000/svg">
              <style>
                {`.peptide-chain { stroke: #3b82f6; stroke-width: 2; fill: none; }
                 .senescent-cell { fill: #ef4444; stroke: #dc2626; stroke-width: 2; }
                 .clearance { fill: #10b981; stroke: #059669; stroke-width: 2; }
                 .text-label { font-family: Arial, sans-serif; font-size: 11px; fill: #374151; text-anchor: middle; }`}
              </style>
              
              {/* Peptide chains */}
              <circle cx="100" cy="50" r="8" fill="#3b82f6"/>
              <circle cx="120" cy="60" r="6" fill="#3b82f6"/>
              <circle cx="140" cy="50" r="8" fill="#3b82f6"/>
              <circle cx="160" cy="65" r="6" fill="#3b82f6"/>
              <line x1="100" y1="50" x2="160" y2="65" className="peptide-chain"/>
              <text x="130" y="90" className="text-label">Peptide Therapeutics</text>
              
              {/* Senescent cell being cleared */}
              <circle cx="300" cy="60" r="25" className="senescent-cell"/>
              <text x="300" y="65" className="text-label" fill="white">Senescent</text>
              <circle cx="320" cy="40" r="15" className="clearance"/>
              <text x="320" y="45" className="text-label" fill="white">Clear</text>
              <text x="300" y="110" className="text-label">Senolytic Action</text>
            </svg>
          </div>
        </div>
      )
    },

    // Slide 18: Neurotrophic Support & Other Key Agents
    {
      title: "Enhancing Brain Resilience: Neurotrophic Support",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-4">
              <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-purple-400">
                <h4 className="font-semibold text-purple-700 mb-3">Lithium Orotate (Microdosing)</h4>
                <div className="space-y-3">
                  <div className="bg-purple-50 p-3 rounded">
                    <h5 className="font-medium text-purple-800">Mechanism</h5>
                    <p className="text-xs text-purple-700">GSK-3β inhibition (↓tau phosphorylation, ↓Aβ, ↑autophagy), BDNF induction</p>
                  </div>
                  <div className="bg-purple-50 p-3 rounded">
                    <h5 className="font-medium text-purple-800">Dosing</h5>
                    <p className="text-xs text-purple-700">1-10mg elemental Li/day (e.g., 5-20mg Li Orotate)</p>
                  </div>
                  <div className="bg-purple-50 p-3 rounded">
                    <h5 className="font-medium text-purple-800">Rationale</h5>
                    <p className="text-xs text-purple-700">Neuroprotection, anti-inflammatory. Orotate form may offer better BBB penetration at lower doses. Chronic microdose prevented memory loss in AD mice.</p>
                  </div>
                  <div className="bg-purple-50 p-3 rounded">
                    <h5 className="font-medium text-purple-800">Monitoring</h5>
                    <p className="text-xs text-purple-700">Renal/thyroid function periodically, esp. at higher end of microdose or long-term</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-green-400">
                <h4 className="font-semibold text-green-700 mb-3">NGF/BDNF Drivers</h4>
                <div className="space-y-3">
                  <div className="bg-green-50 p-3 rounded">
                    <h5 className="font-medium text-green-800">Lion's Mane (Hericium erinaceus)</h5>
                    <ul className="text-xs text-green-700 space-y-1 mt-2">
                      <li>• Erinacines/hericenones stimulate NGF/BDNF</li>
                      <li>• Dosing: 500mg-3g extract/day</li>
                      <li>• Studies: Cognitive improvement in MCI</li>
                    </ul>
                  </div>
                  
                  <div className="bg-green-50 p-3 rounded">
                    <h5 className="font-medium text-green-800">Selank</h5>
                    <ul className="text-xs text-green-700 space-y-1 mt-2">
                      <li>• Anxiolytic, nootropic peptide; upregulates BDNF</li>
                      <li>• Dosing: Intranasal 200-500mcg/nostril daily/bid, cycles</li>
                    </ul>
                  </div>
                  
                  <div className="bg-green-50 p-3 rounded">
                    <h5 className="font-medium text-green-800">Fatty15 (Pentadecanoic Acid, C15:0)</h5>
                    <ul className="text-xs text-green-700 space-y-1 mt-2">
                      <li>• Essential odd-chain SFA</li>
                      <li>• Mechanism: Strengthens cell membranes, improves mitochondrial function, anti-inflammatory, AMPK activation</li>
                      <li>• Higher C15:0 linked to better cognition</li>
                      <li>• Dosing: 100mg C15:0 daily. Synergistic with Omega-3s</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-center">
            <svg width="300" height="150" xmlns="http://www.w3.org/2000/svg">
              <style>
                {`.brain-outline { stroke: #6b7280; stroke-width: 2; fill: #f3f4f6; }
                 .bdnf-icon { fill: #10b981; }
                 .ngf-icon { fill: #3b82f6; }
                 .supplement-icon { fill: #f59e0b; stroke: #d97706; stroke-width: 1; }
                 .text-label { font-family: Arial, sans-serif; font-size: 10px; fill: #374151; text-anchor: middle; }`}
              </style>
              
              <ellipse cx="150" cy="75" rx="80" ry="50" className="brain-outline"/>
              
              <circle cx="120" cy="60" r="8" className="bdnf-icon"/>
              <text x="120" y="65" className="text-label" fill="white" fontSize="8">BDNF</text>
              
              <circle cx="180" cy="60" r="8" className="ngf-icon"/>
              <text x="180" y="65" className="text-label" fill="white" fontSize="8">NGF</text>
              
              <rect x="240" y="40" width="20" height="30" rx="3" className="supplement-icon"/>
              <text x="250" y="80" className="text-label">Supplements</text>
              
              <text x="150" y="140" className="text-label">Brain with Enhanced Neurotrophic Support</text>
            </svg>
          </div>
        </div>
      )
    },

    // Slide 19: Clinical Application - Personalizing Protocols
    {
      title: "Personalizing NDD Protocols",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-4">
              <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-blue-400">
                <h4 className="font-semibold text-blue-700 mb-3">1. Patient Risk Profile</h4>
                <ul className="text-sm text-blue-600 space-y-2">
                  <li>• <strong>Genetics:</strong> APOE4, PRSs guide intensity of prevention</li>
                  <li>• <strong>Cognitive Testing:</strong> Baseline & ongoing (MoCA, etc.) to track</li>
                </ul>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-green-400">
                <h4 className="font-semibold text-green-700 mb-3">2. Clinical Context & Disease Stage</h4>
                <ul className="text-sm text-green-600 space-y-2">
                  <li>• <strong>Early (SCI/MCI):</strong> Aggressively target root causes (metabolic, inflammation, deficiencies)</li>
                  <li>• <strong>Established NDD:</strong> Slow progression, symptom management, continue root cause support</li>
                  <li>• <strong>Post-TBI/Infection:</strong> Acute inflammation control, repair, senolytics</li>
                </ul>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-yellow-400">
                <h4 className="font-semibold text-yellow-700 mb-3">3. Biomarker-Driven Choices</h4>
                <ul className="text-sm text-yellow-600 space-y-2">
                  <li>• Low Plasmalogens → Precursors</li>
                  <li>• High Oxidative Stress → Antioxidants (NAC, GSH), Mitochondrial support</li>
                  <li>• Insulin Resistance → Diet, exercise, metformin/berberine</li>
                  <li>• Inflammation → Anti-inflammatories, gut support, senolytics</li>
                </ul>
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-purple-400">
                <h4 className="font-semibold text-purple-700 mb-3">4. Sequencing & Simplicity</h4>
                <ul className="text-sm text-purple-600 space-y-2">
                  <li>• Start with impactful basics</li>
                  <li>• Avoid "pill fatigue"</li>
                  <li>• Prioritize TLCs</li>
                </ul>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-orange-400">
                <h4 className="font-semibold text-orange-700 mb-3">5. Synergistic Combinations</h4>
                <ul className="text-sm text-orange-600 space-y-2">
                  <li>• Multi-modal approach is key</li>
                  <li>• Example: Aspirin (PPARα) + Selank (BDNF)</li>
                  <li>• Example: Hormones + Peptides</li>
                </ul>
              </div>

              <div className="flex justify-center items-center p-8">
                <svg width="200" height="200" xmlns="http://www.w3.org/2000/svg">
                  <style>
                    {`.funnel { fill: #e5e7eb; stroke: #6b7280; stroke-width: 2; }
                     .input-box { fill: #ddd6fe; stroke: #7c3aed; stroke-width: 1; }
                     .output-box { fill: #dcfce7; stroke: #16a34a; stroke-width: 2; }
                     .text-label { font-family: Arial, sans-serif; font-size: 9px; fill: #374151; text-anchor: middle; }`}
                  </style>
                  
                  {/* Funnel shape */}
                  <polygon points="50,30 150,30 130,100 70,100" className="funnel"/>
                  
                  {/* Input boxes */}
                  <rect x="20" y="10" width="40" height="15" className="input-box"/>
                  <text x="40" y="20" className="text-label">Genetics</text>
                  
                  <rect x="70" y="10" width="40" height="15" className="input-box"/>
                  <text x="90" y="20" className="text-label">Symptoms</text>
                  
                  <rect x="120" y="10" width="50" height="15" className="input-box"/>
                  <text x="145" y="20" className="text-label">Biomarkers</text>
                  
                  {/* Output */}
                  <rect x="60" y="120" width="80" height="25" className="output-box"/>
                  <text x="100" y="130" className="text-label">Personalized</text>
                  <text x="100" y="140" className="text-label">Protocol</text>
                  
                  <text x="100" y="170" className="text-label">Tailored Intervention Strategy</text>
                </svg>
              </div>
            </div>
          </div>
        </div>
      )
    },

    // Slide 20: Clinical Pearls for Practice
    {
      title: "Clinical Pearls",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-4">
              <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-yellow-400">
                <div className="flex items-center mb-3">
                  <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center mr-3">
                    💡
                  </div>
                  <h4 className="font-semibold text-yellow-700">Key Clinical Insights</h4>
                </div>
                <ul className="text-sm text-yellow-600 space-y-3">
                  <li>• <strong>Microdose Lithium Orotate:</strong> Consider for broad neuroprotection AND potential gut-microbiome benefits (GSK-3β, BDNF, SCFAs)</li>
                  <li>• <strong>Periodic Senolytics:</strong> (e.g., Azithromycin Z-Pak q1-3 mos) For high senescent cell burden/neuroinflammation, esp. post-TBI or in established NDDs. Balance with antibiotic stewardship.</li>
                  <li>• <strong>Synergy is Key:</strong> Combine therapies. Fatty15 (C15:0) + Omega-3s for complementary membrane/mitochondrial/anti-inflammatory benefits</li>
                </ul>
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-green-400">
                <div className="flex items-center mb-3">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3">
                    🎯
                  </div>
                  <h4 className="font-semibold text-green-700">Implementation Pearls</h4>
                </div>
                <ul className="text-sm text-green-600 space-y-3">
                  <li>• <strong>Early Intervention Maximizes Impact:</strong> Act on biochemical deficiencies (low plasmalogens, inflammation) before overt symptoms. Declining plasmalogens can predate AD by years.</li>
                  <li>• <strong>Plasmalogens as Longevity Predictor:</strong> Up to 30-year differential in all-cause mortality based on blood plasmalogen levels</li>
                  <li>• <strong>Lifestyle is Foundation (80/20 Rule):</strong> Supplements augment, don't replace</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="flex justify-center">
            <svg width="200" height="100" xmlns="http://www.w3.org/2000/svg">
              <style>
                {`.pearl { fill: #fbbf24; stroke: #f59e0b; stroke-width: 2; }
                 .shine { fill: #fef3c7; opacity: 0.8; }
                 .text-label { font-family: Arial, sans-serif; font-size: 12px; fill: #374151; text-anchor: middle; }`}
              </style>
              
              <circle cx="100" cy="50" r="35" className="pearl"/>
              <ellipse cx="85" cy="40" rx="15" ry="10" className="shine"/>
              <text x="100" y="85" className="text-label">Clinical Pearls</text>
            </svg>
          </div>
        </div>
      )
    },

    // Slide 21: Cautions and Considerations
    {
      title: "Important Cautions",
      content: (
        <div className="space-y-6">
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-lg">
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center mr-4">
                <svg width="20" height="20" fill="currentColor" className="text-yellow-600">
                  <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-yellow-800">Exercise Caution with All Interventions</h3>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-4">
              <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-red-400">
                <h4 className="font-semibold text-red-700 mb-3">Medication/Supplement Risks</h4>
                <ul className="text-sm text-red-600 space-y-3">
                  <li>• <strong>Lithium:</strong> Toxicity, renal/thyroid effects (monitor)</li>
                  <li>• <strong>Azithromycin (Senolytic):</strong> Microbiome, QT prolongation</li>
                  <li>• <strong>Peptides:</strong> Source quality, IGF-1 monitoring (GHRPs), active cancer contraindication (GHRPs)</li>
                  <li>• <strong>Aspirin:</strong> Bleeding risk, GI upset</li>
                  <li>• <strong>BHRT:</strong> Individual risk/benefit, monitoring</li>
                </ul>
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-orange-400">
                <h4 className="font-semibold text-orange-700 mb-3">General Principles</h4>
                <ul className="text-sm text-orange-600 space-y-3">
                  <li>• <strong>Patient Variability:</strong> "One size fits all" does NOT apply. Individualize and monitor</li>
                  <li>• <strong>Avoid Overuse/Overwhelm:</strong> Sequence interventions. Antioxidant paradox (balance)</li>
                  <li>• <strong>Root Cause Focus:</strong> Don't just treat symptoms</li>
                  <li>• <strong>Lifestyle First:</strong> TLCs are non-negotiable</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )
    },

    // Add a comprehensive SHIFT Framework slide
    {
      title: "SHIFT Playbook for Neuro-Longevity Care",
      subtitle: "A Systems Snapshot & Protocol Guide",
      content: (
        <div className="space-y-4">
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">SHIFT FRAMEWORK - SYSTEMS SNAPSHOT & PROTOCOL GUIDE</h3>
            
            <div className="grid grid-cols-6 gap-2 text-sm">
              {/* Headers */}
              <div className="font-semibold text-center bg-gray-100 p-2 rounded">LENS</div>
              <div className="font-semibold text-center bg-red-100 p-2 rounded">S - Sensing</div>
              <div className="font-semibold text-center bg-green-100 p-2 rounded">H - Homeostatic</div>
              <div className="font-semibold text-center bg-yellow-100 p-2 rounded">I - Inflammation</div>
              <div className="font-semibold text-center bg-purple-100 p-2 rounded">F - Fibrosis</div>
              <div className="font-semibold text-center bg-blue-100 p-2 rounded">T - Time-Keeping</div>
              
              {/* Core Drivers */}
              <div className="bg-gray-50 p-2 rounded text-xs">
                <strong>Core ND drivers</strong>
              </div>
              <div className="bg-red-50 p-2 rounded text-xs">
                • Brain "Type-3-DM"<br/>
                • AMPK↓, NAD+/Sirt↓<br/>
                • GH/IGF-1 drift
              </div>
              <div className="bg-green-50 p-2 rounded text-xs">
                • Mitochondrial ATP crisis<br/>
                • Impaired autophagy<br/>
                • ↓GSH / antioxidant activity
              </div>
              <div className="bg-yellow-50 p-2 rounded text-xs">
                • SASP-driven gliosis<br/>
                • NF-κB inflammasome<br/>
                • Gut-derived LPS
              </div>
              <div className="bg-purple-50 p-2 rounded text-xs">
                • Loss of plasmalogens<br/>
                • Myelin breakdown<br/>
                • Vascular stiffening
              </div>
              <div className="bg-blue-50 p-2 rounded text-xs">
                • Amyloid/Tau deposits<br/>
                • Glymphatic clearance<br/>
                • Cortisol/melatonin drift
              </div>
              
              {/* Biomarkers */}
              <div className="bg-gray-50 p-2 rounded text-xs">
                <strong>Sentinel Biomarkers</strong>
              </div>
              <div className="bg-red-50 p-2 rounded text-xs">
                • Fasting insulin, HOMA-IR<br/>
                • IGF-1 (200-300 ng/ml)<br/>
                • CGM trace
              </div>
              <div className="bg-green-50 p-2 rounded text-xs">
                • NAD+/NADH, β-HB<br/>
                • GSH/GSSG, MDA<br/>
                • BDNF, NGF
              </div>
              <div className="bg-yellow-50 p-2 rounded text-xs">
                • hs-CRP, IL-6, TNF-α<br/>
                • Zonulin, LPS-IgA<br/>
                • CD4/CD8 ratio
              </div>
              <div className="bg-purple-50 p-2 rounded text-xs">
                • Serum PlsEtn-DHA<br/>
                • Pulse-wave velocity<br/>
                • MRI-DTI myelin
              </div>
              <div className="bg-blue-50 p-2 rounded text-xs">
                • Amyloid/Tau PET<br/>
                • Salivary cortisol curve<br/>
                • Sleep efficiency greater than 85%
              </div>
              
              {/* Precision Interventions */}
              <div className="bg-gray-50 p-2 rounded text-xs">
                <strong>Precision Interventions</strong>
              </div>
              <div className="bg-red-50 p-2 rounded text-xs">
                • Metformin 500mg bid<br/>
                • CJC-1295 + Ipamorelin<br/>
                • NMN 250-500mg
              </div>
              <div className="bg-green-50 p-2 rounded text-xs">
                • NAC 600-1200mg TID<br/>
                • Liposomal GSH 500mg<br/>
                • Lion's Mane 1-3g/d
              </div>
              <div className="bg-yellow-50 p-2 rounded text-xs">
                • TA-1 1.6mg SC 2x/wk<br/>
                • Omega-3 EPA+DHA 2g<br/>
                • Curcumin 500mg bid
              </div>
              <div className="bg-purple-50 p-2 rounded text-xs">
                • DHA-AAG 900-3600mg<br/>
                • BHRT optimization<br/>
                • Alpha-GPC 600mg
              </div>
              <div className="bg-blue-50 p-2 rounded text-xs">
                • Melatonin 0.3-3mg<br/>
                • Lithium Orotate 5mg<br/>
                • Time-aligned dosing
              </div>
            </div>
            
            <div className="mt-4 text-xs text-gray-600">
              *Doses = typical adult starting points; titrate to labs / tolerance.
            </div>
          </div>
        </div>
      )
    },

    // Slide 23: Conclusion & Future Directions
    {
      title: "Advancing Brain Health: A Forward Look",
      content: (
        <div className="space-y-6">
          <div className="bg-gradient-to-br from-blue-50 to-purple-50 p-8 rounded-lg">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-blue-400">
                  <h3 className="font-semibold text-blue-800 mb-3">Comprehensive Framework</h3>
                  <p className="text-sm text-blue-700">
                    A longevity and functional medicine approach offers a proactive, comprehensive framework for NDDs
                  </p>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-green-400">
                  <h3 className="font-semibold text-green-800 mb-3">Key Focus Areas</h3>
                  <ul className="text-sm text-green-700 space-y-1">
                    <li>• Cellular health & metabolic optimization</li>
                    <li>• Inflammation control & hormonal balance</li>
                    <li>• Gut-brain integrity & structural support</li>
                    <li>• Chronobiology optimization</li>
                  </ul>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-purple-400">
                  <h3 className="font-semibold text-purple-800 mb-3">Emerging Research</h3>
                  <p className="text-sm text-purple-700">
                    Peptides, senolytics, and advanced biomarkers continue to expand our therapeutic toolkit
                  </p>
                </div>
              </div>

              <div className="flex flex-col justify-center items-center">
                <svg width="300" height="250" xmlns="http://www.w3.org/2000/svg">
                  <style>
                    {`.brain-path { fill: none; stroke: #3b82f6; stroke-width: 2; }
                     .glow { fill: #fbbf24; opacity: 0.6; }
                     .connection { stroke: #10b981; stroke-width: 1.5; opacity: 0.8; }
                     .text-label { font-family: Arial, sans-serif; font-size: 12px; fill: #374151; text-anchor: middle; }`}
                  </style>
                  
                  {/* Brain outline */}
                  <path d="M60 120 Q 80 80, 120 90 Q 160 70, 200 90 Q 240 80, 260 120 Q 250 160, 200 170 Q 150 180, 100 170 Q 70 150, 60 120 Z" className="brain-path"/>
                  
                  {/* Glowing nodes */}
                  <circle cx="100" cy="120" r="8" className="glow"/>
                  <circle cx="150" cy="100" r="8" className="glow"/>
                  <circle cx="200" cy="120" r="8" className="glow"/>
                  <circle cx="175" cy="150" r="8" className="glow"/>
                  <circle cx="125" cy="150" r="8" className="glow"/>
                  
                  {/* Neural connections */}
                  <line x1="100" y1="120" x2="150" y2="100" className="connection"/>
                  <line x1="150" y1="100" x2="200" y2="120" className="connection"/>
                  <line x1="200" y1="120" x2="175" y2="150" className="connection"/>
                  <line x1="175" y1="150" x2="125" y2="150" className="connection"/>
                  <line x1="125" y1="150" x2="100" y2="120" className="connection"/>
                  <line x1="150" y1="100" x2="175" y2="150" className="connection"/>
                  <line x1="150" y1="100" x2="125" y2="150" className="connection"/>
                  
                  <text x="150" y="210" className="text-label">Optimized Neural Networks</text>
                  <text x="150" y="230" className="text-label">Enhanced Healthspan & Cognition</text>
                </svg>
                
                <div className="mt-6 text-center">
                  <div className="bg-gradient-to-r from-green-600 to-blue-600 text-white p-4 rounded-lg">
                    <h4 className="text-lg font-semibold">Empowering Patients</h4>
                    <p className="text-sm mt-2">
                      This approach empowers individuals to take an active role in their brain health and longevity
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },

    // Slide 24: Q&A
    {
      title: "Questions & Discussion",
      content: (
        <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
          <div className="bg-gradient-to-br from-blue-50 to-indigo-100 p-12 rounded-2xl shadow-lg max-w-4xl">
            <h1 className="text-4xl font-bold text-gray-900 mb-6">
              Questions & Discussion
            </h1>
            <p className="text-xl text-gray-700 mb-8">
              Thank you for your attention. I welcome your questions and insights.
            </p>
            <div className="text-lg text-gray-600">
              Let's explore how these longevity and functional medicine approaches can be integrated into your clinical practice.
            </div>
          </div>
        </div>
      )
    },

    // Slide 25: Key References (Part 1)
    {
      title: "Selected References",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 p-6 rounded-lg border-l-4 border-blue-400">
            <h3 className="font-semibold text-blue-800 mb-3">Key References Supporting These Concepts</h3>
            <p className="text-sm text-blue-700">
              Due to space limitations, this represents a selection of the most impactful references supporting newer concepts in neurodegenerative disease management.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-4">
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h4 className="font-semibold text-gray-800 mb-3">Foundational Research</h4>
                <ul className="text-xs text-gray-700 space-y-2">
                  <li>• Frontiers in Neuroscience. (2025). Significance of gene therapy in neurodegenerative diseases. (Ref 1)</li>
                  <li>• Frontiers in Aging Neuroscience. (2025). Multi-pathway blood biomarkers... (Ref 2)</li>
                  <li>• PMC. (2025). The Role of Glial Cell Senescence in Alzheimer's Disease. (Ref 3, 5)</li>
                  <li>• PMC. (2025). NF-κB as an Inducible Regulator of Inflammation... (Ref 6)</li>
                  <li>• PubMed. (2025). Autophagy, aging, and age-related neurodegeneration. (Ref 8)</li>
                  <li>• PMC. (2025). The Role of BDNF as a Biomarker... (Ref 13)</li>
                  <li>• PMC. (2025). Brain-Derived Neurotropic Factor in Neurodegenerative Disorders. (Ref 14)</li>
                </ul>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h4 className="font-semibold text-gray-800 mb-3">Therapeutic Innovations</h4>
                <ul className="text-xs text-gray-700 space-y-2">
                  <li>• Frontiers in Nutrition. (2025). Acute effects of a standardised extract of Hericium erinaceus... (Ref 15)</li>
                  <li>• PMC. (2025). Effects of GH/IGF on the Aging Mitochondria. (Ref 18)</li>
                  <li>• PubMed. (2025). Elamipretide: A Review of Its Structure, Mechanism of Action... (Ref 28)</li>
                  <li>• PMC. (2025). Elamipretide (SS-31) improves mitochondrial dysfunction... (Ref 29)</li>
                </ul>
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h4 className="font-semibold text-gray-800 mb-3">Plasmalogen Research</h4>
                <ul className="text-xs text-gray-700 space-y-2">
                  <li>• PMC. (2019). Plasmalogens and Alzheimer's disease: a review. (Ref 31, 37)</li>
                  <li>• PubMed. (2021). Therapeutic Efficacy of Plasmalogens for Alzheimer's Disease... (Ref 35)</li>
                  <li>• PMC. (2022). Targeted Plasmalogen Supplementation: Effects on Blood... (Ref 40)</li>
                </ul>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h4 className="font-semibold text-gray-800 mb-3">Clinical Applications</h4>
                <ul className="text-xs text-gray-700 space-y-2">
                  <li>• PMC. (2015). The Role of Oxidative Stress in Neurodegenerative Diseases. (Ref 47)</li>
                  <li>• PMC. (2019). A Review of Dietary (Phyto)Nutrients for Glutathione Support. (Ref 48)</li>
                  <li>• PMC. (2018). Oral supplementation with liposomal glutathione elevates body... (Ref 53)</li>
                  <li>• PMC. (2019). Neuroprotective Role of Steroidal Sex Hormones: An Overview. (Ref 54)</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )
    },

    // Slide 26: Key References (Part 2)
    {
      title: "Selected References (Continued)",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-4">
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h4 className="font-semibold text-gray-800 mb-3">Hormonal & Metabolic Interventions</h4>
                <ul className="text-xs text-gray-700 space-y-2">
                  <li>• Frontiers in Dementia. (2024). Hormone replacement therapy, menopausal age and lifestyle... (Ref 59, 62)</li>
                  <li>• PMC. (2021). PPARα Between Aspirin and Plaque Clearance. (Ref 63)</li>
                  <li>• PubMed. (2024). Type 3 diabetes and metabolic reprogramming of brain neurons... (Ref 71)</li>
                  <li>• PubMed. (2022). Peripheral apoE4 enhances Alzheimer's pathology... (Ref 73)</li>
                </ul>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h4 className="font-semibold text-gray-800 mb-3">Gut-Brain Axis & Microbiome</h4>
                <ul className="text-xs text-gray-700 space-y-2">
                  <li>• PubMed. (2024). Neurodegenerative Disorders and the Gut-Microbiome-Brain Axis... (Ref 65)</li>
                  <li>• PMC. (2022). The Actions of Lithium on Glaucoma and Other Senile... (Ref 68)</li>
                  <li>• PMC. (2014). Neuroprotective Effects of Lithium: Implications for the Treatment of AD... (Ref 69)</li>
                  <li>• PMC. (2015). Chronic Microdose Lithium Treatment Prevented Memory Loss... (Ref 93)</li>
                </ul>
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h4 className="font-semibold text-gray-800 mb-3">Emerging Therapeutics</h4>
                <ul className="text-xs text-gray-700 space-y-2">
                  <li>• Prodrome Sciences. ProdromeScan™. (Ref 100)</li>
                  <li>• Innerbody. (2025). CJC-1295 + Ipamorelin... (Ref 101)</li>
                  <li>• MDPI. (2025). Multifunctionality and Possible Medical Application of the BPC 157 Peptide... (Ref 113)</li>
                  <li>• PMC. (2018). Azithromycin and Roxithromycin define a new family of "senolytic... (Ref 118, 120)</li>
                </ul>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h4 className="font-semibold text-gray-800 mb-3">Nutritional & Lifestyle Factors</h4>
                <ul className="text-xs text-gray-700 space-y-2">
                  <li>• Amazon.com. Fatty15 C15:0 Pentadecanoic Acid Supplement... (Ref 139)</li>
                  <li>• PMC. (2019). Omega-3 Fatty Acids and Neurodegenerative Diseases... (Ref 147)</li>
                  <li>• PMC. (2025). Considerations in the clinical use of amyloid PET and CSF biomarkers... (Ref 153)</li>
                </ul>
              </div>

              <div className="bg-blue-50 p-6 rounded-lg border-l-4 border-blue-400">
                <h4 className="font-semibold text-blue-800 mb-2">Note on References</h4>
                <p className="text-xs text-blue-700">
                  This represents a curated selection of key references supporting the concepts presented. 
                  The complete reference list contains additional supporting evidence for all interventions and mechanisms discussed.
                </p>
              </div>
            </div>
          </div>
        </div>
      )
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Breadcrumb Navigation */}
      <nav className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <Link href="/mentorship" className="flex items-center text-gray-600 hover:text-blue-600 transition-colors">
                <Home className="h-4 w-4 mr-2" />
                Mentorship
              </Link>
              <span className="text-gray-400">/</span>
              <div className="flex items-center text-blue-600">
                <Book className="h-4 w-4 mr-2" />
                Neurodegenerative Disease
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Neurodegenerative Disease: A Longevity Medicine Approach
          </h1>
          <p className="text-gray-600">
            Slide {currentSlide + 1} of {slides.length}
          </p>
        </div>

        {/* Slide Content */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8 min-h-[500px]">
          <div className="space-y-6">
            {slides[currentSlide].title && (
              <h2 className="text-2xl font-bold text-gray-900">
                {slides[currentSlide].title}
              </h2>
            )}
            {slides[currentSlide].subtitle && (
              <h3 className="text-lg text-gray-700 font-medium">
                {slides[currentSlide].subtitle}
              </h3>
            )}
            <div className="text-gray-700">
              {slides[currentSlide].content}
            </div>
          </div>
        </div>

        {/* Navigation Controls */}
        <div className="flex justify-between items-center mb-8">
          <button
            onClick={prevSlide}
            disabled={currentSlide === 0}
            className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
              currentSlide === 0
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            <ChevronLeft className="h-5 w-5 mr-2" />
            Previous
          </button>

          <div className="flex space-x-2">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-3 h-3 rounded-full transition-colors ${
                  index === currentSlide ? 'bg-blue-600' : 'bg-gray-300'
                }`}
              />
            ))}
          </div>

          <button
            onClick={nextSlide}
            disabled={currentSlide === slides.length - 1}
            className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
              currentSlide === slides.length - 1
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            Next
            <ChevronRight className="h-5 w-5 ml-2" />
          </button>
        </div>

        {/* Audio Lecture Section */}
        <div className="mt-12 bg-white rounded-xl shadow-lg p-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Audio Lecture</h3>
          <div className="bg-blue-50 p-6 rounded-lg">
            <iframe
              src="https://share.descript.com/embed/efvmm5FWso7"
              width="100%"
              height="360"
              frameBorder="0"
              allowFullScreen
              className="rounded-lg shadow-md"
              title="Neurodegenerative Disease Audio Lecture"
            ></iframe>
          </div>
          <p className="text-sm text-gray-600 mt-4">
            Listen to the comprehensive audio lecture covering neurodegenerative disease prevention, mechanisms, and therapeutic strategies through the lens of longevity and functional medicine.
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mt-8">
          <div className="bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${((currentSlide + 1) / slides.length) * 100}%` }}
            ></div>
          </div>
          <p className="text-center text-sm text-gray-600 mt-2">
            Progress: {Math.round(((currentSlide + 1) / slides.length) * 100)}%
          </p>
        </div>
      </div>
    </div>
  );
}